/**
 * Decisions Store
 *
 * First-class decision objects: both manually-entered decisions and
 * auto-linked decisions (from audit events) live here.
 *
 * SUPABASE-ONLY PERSISTENCE (no localStorage).
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  isSupabaseAvailable,
  getCurrentWorkspaceId,
  createInitialSyncState,
  type SyncState,
} from '../../lib/supabaseSync';
import { useSpaceStore } from './spaceStore';
import type {
  Decision,
  DecisionRelatedObject,
  CreateDecisionPayload,
  UpdateDecisionPayload,
  DecisionClass,
} from '../../types/prioritization/decisions';
import { computeHealthStatus } from '../../lib/decisionHealth';

/** Turn a raw database error into a reason a person can act on. */
function humanizeSyncError(message: string): string {
  if (/row-level security|violates row-level/i.test(message)) {
    return 'You are signed out or do not have permission to change decisions in this workspace.';
  }
  if (/JSON object requested|0 rows|no rows/i.test(message)) {
    return 'The decision was not found, or you do not have permission to change it.';
  }
  return message;
}

const TABLE = 'decisions';

/**
 * A governed decision RPC's failure, as a sentence a person can act on — the
 * raised error, or the `{ success: false, error, remedy }` envelope. Null when
 * the call succeeded.
 */
export function rpcRefusal(error: { message: string } | null, data: unknown): string | null {
  if (error) return humanizeSyncError(error.message);
  const env = data as { success?: boolean; error?: string; remedy?: string } | null;
  if (!env || env.success !== true) {
    const reason = env?.error ?? 'The server did not confirm the change.';
    return env?.remedy ? `${reason} — ${env.remedy}.` : reason;
  }
  return null;
}

type DecisionFieldPayload = Partial<CreateDecisionPayload & UpdateDecisionPayload> & { spaceId?: string };

/** camelCase payload → the snake_case keys record_decision / update_decision take. Undefined keys are left out. */
export function decisionFieldsFromPayload(payload: DecisionFieldPayload): Record<string, unknown> {
  const map: Record<string, string> = {
    title: 'title',
    body: 'body',
    decisionClass: 'decision_class',
    subjectKind: 'subject_kind',
    impact: 'impact',
    status: 'status',
    rationaleContext: 'rationale_context',
    rationaleReasoning: 'rationale_reasoning',
    rationaleAlternatives: 'rationale_alternatives',
    reconsiderationConditions: 'reconsideration_conditions',
    reviewByDate: 'review_by_date',
    itemId: 'item_id',
    sessionId: 'session_id',
    cycleId: 'cycle_id',
    itemPoolId: 'item_pool_id',
    spaceId: 'space_id',
    parentDecisionId: 'parent_decision_id',
    supersedesDecisionId: 'supersedes_decision_id',
    site: 'site',
    relatedObjects: 'related_objects',
    tags: 'tags',
    decidedAt: 'decided_at',
    originModule: 'origin_module',
    source: 'source',
    sourceEventId: 'source_event_id',
  };
  const out: Record<string, unknown> = {};
  for (const [camel, snake] of Object.entries(map)) {
    const value = (payload as Record<string, unknown>)[camel];
    if (value !== undefined) out[snake] = value;
  }
  return out;
}

// ============================================================================
// DB row mapper
// ============================================================================

function rowToDecision(row: Record<string, unknown>): Decision {
  const rationaleContext        = (row.rationale_context as string | null) ?? null;
  const rationaleReasoning      = (row.rationale_reasoning as string | null) ?? null;
  const rationaleAlternatives   = (row.rationale_alternatives as string | null) ?? null;
  const reconsiderationConditions = (row.reconsideration_conditions as string | null) ?? null;
  const reviewByDate            = (row.review_by_date as string | null) ?? null;

  const d: Decision = {
    id:            row.id as string,
    workspaceId:   row.workspace_id as string,
    title:         row.title as string,
    body:          (row.body as string | null) ?? null,
    decisionKind:  (row.decision_kind as Decision['decisionKind']) ?? 'manual',
    decisionClass: (row.decision_class as Decision['decisionClass']) ?? null,
    source:        (row.source as string | null) ?? null,
    sourceEventId: (row.source_event_id as string | null) ?? null,
    itemId:        (row.item_id as string | null) ?? null,
    sessionId:     (row.session_id as string | null) ?? null,
    cycleId:       (row.cycle_id as string | null) ?? null,
    itemPoolId:    (row.item_pool_id as string | null) ?? null,
    decidedBy:     (row.decided_by as string | null) ?? null,
    decidedAt:     row.decided_at as string,
    impact:        (row.impact as Decision['impact']) ?? 'medium',
    status:        (row.status as Decision['status']) ?? 'active',
    tags:           (row.tags as string[]) ?? [],
    relatedObjects: (row.related_objects as DecisionRelatedObject[] | null) ?? [],
    rationaleContext,
    rationaleReasoning,
    rationaleAlternatives,
    reconsiderationConditions,
    reviewByDate,
    createdAt:      row.created_at as string,
    updatedAt:      row.updated_at as string,
    originModule:   (row.origin_module as Decision['originModule']) ?? 'prioritization',
    spaceId:        (row.space_id as string | null) ?? null,
    subjectKind:          (row.subject_kind as Decision['subjectKind']) ?? null,
    parentDecisionId:     (row.parent_decision_id as string | null) ?? null,
    supersedesDecisionId: (row.supersedes_decision_id as string | null) ?? null,
    site:                 (row.site as string | null) ?? null,
    // Persisted since migration 568 (snapshotted at write time); may still be
    // null on old rows — display falls back to the live actor lookup.
    decidedByName:  (row.decided_by_name as string | null) ?? null,
    itemTitle:      null,
  };
  d.healthStatus = computeHealthStatus(d);
  return d;
}

// ============================================================================
// State interface
// ============================================================================

interface DecisionsState {
  decisions: Decision[];
  syncState: SyncState;
  /**
   * Why the most recent create / update / delete failed, in words a person can
   * act on. Cleared at the start of each write. The write methods still return
   * null / false on failure; callers read this to tell the user why.
   */
  lastError: string | null;

  // Load
  fetchDecisions: () => Promise<void>;

  // CRUD
  createDecision: (payload: CreateDecisionPayload) => Promise<Decision | null>;
  updateDecision: (id: string, payload: UpdateDecisionPayload) => Promise<Decision | null>;
  deleteDecision: (id: string, reason?: string) => Promise<boolean>;

  // Queries
  getDecision: (id: string) => Decision | undefined;
  getDecisionsByKind: (kind: Decision['decisionKind']) => Decision[];
  getDecisionsByItem: (itemId: string) => Decision[];
  getDecisionsByClass: (cls: DecisionClass) => Decision[];
  getDecisionsByPool: (itemPoolId: string) => Decision[];
  getStaleDecisions: () => Decision[];
}

// ============================================================================
// Store
// ============================================================================

export const useDecisionsStore = create<DecisionsState>()((set, get) => ({
  decisions: [],
  lastError: null,
  syncState: createInitialSyncState(),

  // --------------------------------------------------------------------------
  // Fetch
  // --------------------------------------------------------------------------

  fetchDecisions: async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;

    set((s) => ({ syncState: { ...s.syncState, isLoading: true, syncError: null } }));

    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('decided_at', { ascending: false });

    if (error) {
      console.error('[DecisionsStore] fetch error:', error.message);
      set((s) => ({ syncState: { ...s.syncState, isLoading: false, syncError: error.message } }));
      return;
    }

    const decisions = (data ?? []).map((r) => rowToDecision(r as Record<string, unknown>));
    set({ decisions, syncState: { ...get().syncState, isLoading: false, lastSyncAt: new Date() } });
  },

  // --------------------------------------------------------------------------
  // Create
  // --------------------------------------------------------------------------

  createDecision: async (payload) => {
    set({ lastError: null });
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      set({ lastError: 'No workspace is selected. Select a workspace and try again.' });
      return null;
    }

    // Never hand back a local-only row as if it were saved: a decision that
    // lives only in this tab disappears on reload. No database, no decision.
    const supabase = isSupabaseAvailable() ? getSafeSupabaseClient() : null;
    if (!supabase) {
      set({ lastError: 'Not connected to the database, so the decision was not saved.' });
      return null;
    }

    // One governed write (record_decision, mig 765): the server stamps the
    // decider (the signed-in person), checks every referenced record is this
    // workspace's, records the governance event, and — when this decision
    // supersedes another — moves that one to superseded in the same
    // transaction (canon 27 §6).
    const fields = decisionFieldsFromPayload({
      ...payload,
      // Stamp the active collaboration space (mirrors items/sessions pattern).
      spaceId: useSpaceStore.getState().currentSpaceId ?? undefined,
    });
    // record_decision takes the title as its own parameter and validates p_fields
    // against an allow-list that does not include 'title'. Sending it in both
    // places made the server refuse every hand-recorded decision with
    // "These fields cannot be set here: title." — so Record Decision never
    // saved anything. The title still travels, as p_title.
    delete fields.title;
    const { data, error } = await supabase.rpc('record_decision', {
      p_workspace_id: workspaceId,
      p_title: payload.title,
      p_fields: fields,
    });
    const refusal = rpcRefusal(error, data);
    if (refusal) {
      console.error('[DecisionsStore] create error:', refusal);
      set({ lastError: refusal });
      return null;
    }

    const result = data as { decision: Record<string, unknown>; superseded_decision_id?: string | null };
    const saved = rowToDecision(result.decision);
    const supersededId = result.superseded_decision_id ?? null;
    set((s) => ({
      decisions: [
        saved,
        ...s.decisions.map((d) => (d.id === supersededId ? { ...d, status: 'superseded' as const } : d)),
      ],
    }));
    return saved;
  },

  // --------------------------------------------------------------------------
  // Update
  // --------------------------------------------------------------------------

  updateDecision: async (id, payload) => {
    set({ lastError: null });
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ lastError: 'Not connected to the database, so the change was not saved.' });
      return null;
    }

    // update_decision (mig 765): the decider or a workspace admin (canon 27 §6
    // rule 2); content edits only while the decision is a manual, active draft.
    const { data, error } = await supabase.rpc('update_decision', {
      p_decision_id: id,
      p_changes: decisionFieldsFromPayload(payload),
    });
    const refusal = rpcRefusal(error, data);
    if (refusal) {
      console.error('[DecisionsStore] update error:', refusal);
      set({ lastError: refusal });
      return null;
    }

    const updated = rowToDecision((data as { decision: Record<string, unknown> }).decision);
    set((s) => ({ decisions: s.decisions.map((d) => (d.id === id ? updated : d)) }));
    return updated;
  },

  // --------------------------------------------------------------------------
  // Delete
  // --------------------------------------------------------------------------

  deleteDecision: async (id, reason) => {
    set({ lastError: null });
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ lastError: 'Not connected to the database, so nothing was deleted.' });
      return false;
    }

    // delete_decision (mig 765): manual, still-active decisions only, by the
    // decider or a workspace admin; the governance event keeps the full row.
    const { data, error } = await supabase.rpc('delete_decision', {
      p_decision_id: id,
      p_reason: reason ?? undefined,
    });
    const refusal = rpcRefusal(error, data);
    if (refusal) {
      console.error('[DecisionsStore] delete error:', refusal);
      set({ lastError: refusal });
      return false;
    }

    set((s) => ({ decisions: s.decisions.filter((d) => d.id !== id) }));
    return true;
  },

  // --------------------------------------------------------------------------
  // Queries
  // --------------------------------------------------------------------------

  getDecision: (id) => get().decisions.find((d) => d.id === id),

  getDecisionsByKind: (kind) => get().decisions.filter((d) => d.decisionKind === kind),

  getDecisionsByItem: (itemId) => get().decisions.filter((d) => d.itemId === itemId),

  getDecisionsByClass: (cls) => get().decisions.filter((d) => d.decisionClass === cls),

  getDecisionsByPool: (itemPoolId) => get().decisions.filter((d) => d.itemPoolId === itemPoolId),

  getStaleDecisions: () =>
    get().decisions.filter((d) => d.healthStatus === 'stale' || d.healthStatus === 'orphaned'),
}));
